import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { cn } from "@/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Textarea } from "@/components/Base/Textarea";

export type TextareaFieldProps<TFieldValues extends FieldValues = FieldValues> =
  {
    form: UseFormReturn<TFieldValues>;
    name: Path<TFieldValues>;
    label?: string;
    description?: string;
    placeholder?: string;
    disabled?: boolean;
    defaultValue?: PathValue<TFieldValues, Path<TFieldValues>>;
    onChange?: (value: string) => void;
    className?: string;
    rows?: number;
  };

export const TextareaField = <TFieldValues extends FieldValues = FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
  disabled,
  defaultValue = "" as PathValue<TFieldValues, Path<TFieldValues>>,
  onChange,
  className,
  rows = 3,
}: TextareaFieldProps<TFieldValues>) => {
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    form.setValue(name, value as PathValue<TFieldValues, Path<TFieldValues>>, {
      shouldValidate: true,
    });

    if (onChange) {
      onChange(value);
    }
  };

  return (
    <FormField
      control={form.control}
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Textarea
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
